import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  TouchableOpacity,
  ScrollView,
  TextInput as RNTextInput,
  Alert,
  ActivityIndicator,
  Platform,
  Modal,
  FlatList,
} from 'react-native';
import type { TextStyle, ViewStyle, ImageStyle } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Text, useTheme, useThemeName } from '@tamagui/core';
import { YStack, XStack } from '@tamagui/stacks';
import { X, Plus, Lock, LockOpen, Star } from 'lucide-react-native';
import { useIsMobileLayout } from '@/hooks/useWindowWidth';

// Types
import type { RootStackScreenProps } from '@/types/navigation';

// Components
import { Screen, ScreenHeader, CitySearchModal } from '@/components';
import AuthenticatedImage from '@/components/AuthenticatedImage';

// API
import {
  useMyProfile,
  useUpdateProfile,
  useGenders,
  useHobbies,
  useDeleteUserPhoto,
  useUploadUserPhoto,
  useSetUserPhotoDefault,
  useLockUserPhoto,
  useUnlockUserPhoto,
  useLogout,
  OtherCityData,
  profileKeys,
} from '@/api';
import { useQueryClient } from '@tanstack/react-query';
import type { UserProfileView } from '@/api/types';
import { useCities } from '@/api/hooks/useLocations';
import { useAuth } from '@/contexts/AuthContext';
import { resetToAuth } from '@/navigation/navigationRef';

// Constants
import { COLORS } from '@/constants';

// Utils
import { showSuccessToast, showErrorToast } from '@/utils/toast';

type Props = RootStackScreenProps<'MyProfile'>;

const MyProfileScreen: React.FC<Props> = ({ navigation }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const themeName = useThemeName();
  const isDark = themeName === 'dark';
  const isMobileLayout = useIsMobileLayout();
  const queryClient = useQueryClient();

  // API Hooks
  const {
    data: profile,
    isLoading: isLoadingProfile,
    refetch,
  } = useMyProfile();
  const { data: genders = [], isLoading: isLoadingGenders } = useGenders();
  const { data: hobbies = [], isLoading: isLoadingHobbies } = useHobbies();
  const { data: cities = [], isLoading: isLoadingCities } = useCities(1);

  const updateProfile = useUpdateProfile({
    onSuccess: () => {
      showSuccessToast(
        t('screens.myProfile.saveSuccess', 'Zapisano'),
        t(
          'screens.myProfile.saveSuccessMessage',
          'Profil został zaktualizowany',
        ),
      );
      refetch();
    },
    onError: error => {
      showErrorToast(
        t('common.error', 'Błąd'),
        error.message ||
          t('screens.myProfile.saveError', 'Nie udało się zapisać profilu'),
      );
    },
  });

  const deletePhoto = useDeleteUserPhoto({
    onMutate: async photoId => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: profileKeys.me() });

      // Snapshot the previous value
      const previousProfile = queryClient.getQueryData<UserProfileView>(
        profileKeys.me(),
      );

      // Optimistically update to the new value
      queryClient.setQueryData<UserProfileView>(profileKeys.me(), oldData => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          photos: oldData.photos?.filter(p => p.id !== photoId) || [],
        };
      });

      // Return a context object with the snapshotted value
      return { previousProfile };
    },
    onSuccess: () => {
      showSuccessToast(
        t('screens.myProfile.photoDeleted', 'Usunięto'),
        t('screens.myProfile.photoDeletedMessage', 'Zdjęcie zostało usunięte'),
      );
    },
    onError: (error, _photoId, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (
        context &&
        typeof context === 'object' &&
        context !== null &&
        'previousProfile' in context
      ) {
        const typedContext = context as { previousProfile?: UserProfileView };
        if (typedContext.previousProfile) {
          queryClient.setQueryData(
            profileKeys.me(),
            typedContext.previousProfile,
          );
        }
      }
      showErrorToast(
        t('common.error', 'Błąd'),
        error.message ||
          t(
            'screens.myProfile.photoDeleteError',
            'Nie udało się usunąć zdjęcia',
          ),
      );
    },
    onSettled: () => {
      // Always invalidate queries after error or success to ensure sync with server
      queryClient.invalidateQueries({ queryKey: profileKeys.me() });
    },
  });

  const uploadPhoto = useUploadUserPhoto({
    onSuccess: () => {
      showSuccessToast(
        t('screens.myProfile.photoUploaded', 'Dodano'),
        t('screens.myProfile.photoUploadedMessage', 'Zdjęcie zostało dodane'),
      );
      refetch();
    },
    onError: error => {
      showErrorToast(
        t('common.error', 'Błąd'),
        error.message ||
          t(
            'screens.myProfile.photoUploadError',
            'Nie udało się dodać zdjęcia',
          ),
      );
    },
  });

  // Form State
  const [nickName, setNickName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [bio, setBio] = useState('');
  const [selectedGenderId, setSelectedGenderId] = useState<number | null>(null);
  const [selectedGenderPreferenceIds, setSelectedGenderPreferenceIds] =
    useState<number[]>([]);
  const [selectedHobbyIds, setSelectedHobbyIds] = useState<number[]>([]);
  const [selectedCityIds, setSelectedCityIds] = useState<number[]>([]);
  const [otherCity, setOtherCity] = useState<OtherCityData | null>(null);
  const [isCityModalVisible, setIsCityModalVisible] = useState(false);

  // Date of birth picker state
  const [dateOfBirth, setDateOfBirth] = useState<Date | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDay, setSelectedDay] = useState(1);
  const [selectedMonth, setSelectedMonth] = useState(1);
  const [selectedYear, setSelectedYear] = useState(2000);

  // Photo upload refs (for web)
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Lock/Unlock photo mutations
  const lockPhoto = useLockUserPhoto({
    onSuccess: () => {
      showSuccessToast(
        t('screens.myProfile.photoLocked', 'Zablokowano'),
        t(
          'screens.myProfile.photoLockedMessage',
          'Zdjęcie zostało zablokowane',
        ),
      );
    },
    onError: error => {
      showErrorToast(
        t('common.error', 'Błąd'),
        error.message ||
          t(
            'screens.myProfile.photoLockError',
            'Nie udało się zablokować zdjęcia',
          ),
      );
    },
  });

  const unlockPhoto = useUnlockUserPhoto({
    onSuccess: () => {
      showSuccessToast(
        t('screens.myProfile.photoUnlocked', 'Odblokowano'),
        t(
          'screens.myProfile.photoUnlockedMessage',
          'Zdjęcie zostało odblokowane',
        ),
      );
    },
    onError: error => {
      showErrorToast(
        t('common.error', 'Błąd'),
        error.message ||
          t(
            'screens.myProfile.photoUnlockError',
            'Nie udało się odblokować zdjęcia',
          ),
      );
    },
  });

  // Initialize form with profile data
  useEffect(() => {
    if (profile) {
      setNickName(profile.nickName || '');
      setFirstName(profile.firstName || '');
      setLastName(profile.lastName || '');
      setBio(profile.bio || '');
      setSelectedGenderId(profile.genderId);
      setSelectedGenderPreferenceIds(profile.genderPreferenceIds || []);
      setSelectedHobbyIds(profile.hobbyIds || []);
      setSelectedCityIds(profile.cityIds || []);
      if (profile.dateOfBirth) {
        const birthDate = new Date(profile.dateOfBirth);
        setDateOfBirth(birthDate);
        setSelectedDay(birthDate.getDate());
        setSelectedMonth(birthDate.getMonth() + 1);
        setSelectedYear(birthDate.getFullYear());
      }
    }
  }, [profile]);

  // Handlers
  const handleGoBack = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const handleSave = useCallback(() => {
    updateProfile.mutate({
      firstName: firstName || null,
      lastName: lastName || null,
      bio,
      genderId: selectedGenderId,
      genderPreferenceIds: selectedGenderPreferenceIds,
      hobbyIds: selectedHobbyIds,
      cityIds: selectedCityIds,
      otherCity: otherCity,
      dateOfBirth: dateOfBirth ? dateOfBirth.toISOString().split('T')[0] : null,
    });
  }, [
    firstName,
    lastName,
    bio,
    selectedGenderId,
    selectedGenderPreferenceIds,
    selectedHobbyIds,
    selectedCityIds,
    otherCity,
    dateOfBirth,
    updateProfile,
  ]);

  const handleDeletePhoto = useCallback(
    (photoId: number) => {
      deletePhoto.mutate(photoId);
    },
    [deletePhoto],
  );

  const setDefaultPhoto = useSetUserPhotoDefault({
    onMutate: async ({ photoId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: profileKeys.me() });

      // Snapshot the previous value
      const previousProfile = queryClient.getQueryData<UserProfileView>(
        profileKeys.me(),
      );

      // Optimistically update: set this photo as default, unset others, and sort
      queryClient.setQueryData<UserProfileView>(profileKeys.me(), oldData => {
        if (!oldData) return oldData;
        const updatedPhotos =
          oldData.photos?.map(p => ({
            ...p,
            isDefault: p.id === photoId,
          })) || [];
        // Sort: default first
        updatedPhotos.sort((a, b) => {
          if (a.isDefault) return -1;
          if (b.isDefault) return 1;
          return 0;
        });
        return {
          ...oldData,
          photos: updatedPhotos,
          defaultPhotoId: photoId,
        };
      });

      return { previousProfile };
    },
    onSuccess: () => {
      showSuccessToast(
        t('screens.myProfile.defaultPhotoSet', 'Ustawiono'),
        t(
          'screens.myProfile.defaultPhotoSetMessage',
          'Zdjęcie zostało ustawione jako domyślne',
        ),
      );
    },
    onError: (error, _variables, context) => {
      // Rollback on error
      if (
        context &&
        typeof context === 'object' &&
        context !== null &&
        'previousProfile' in context
      ) {
        const typedContext = context as { previousProfile?: UserProfileView };
        if (typedContext.previousProfile) {
          queryClient.setQueryData(
            profileKeys.me(),
            typedContext.previousProfile,
          );
        }
      }
      showErrorToast(
        t('common.error', 'Błąd'),
        error.message ||
          t(
            'screens.myProfile.defaultPhotoError',
            'Nie udało się ustawić domyślnego zdjęcia',
          ),
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: profileKeys.me() });
    },
  });

  const handleSetDefaultPhoto = useCallback(
    (photoId: number) => {
      // Set this photo as default (API will handle unsetting others)
      setDefaultPhoto.mutate({
        photoId,
        data: { isDefault: true },
      });
    },
    [setDefaultPhoto],
  );

  const { logout: authLogout } = useAuth();
  const logout = useLogout({
    onSuccess: async () => {
      await authLogout();
      resetToAuth();
    },
    onError: error => {
      showErrorToast(
        t('common.error', 'Błąd'),
        error.message ||
          t('screens.myProfile.logoutError', 'Nie udało się wylogować'),
      );
    },
  });

  const handleLogout = useCallback(() => {
    logout.mutate();
  }, [logout]);

  const handleAddPhoto = useCallback(() => {
    if (Platform.OS === 'web') {
      fileInputRef.current?.click();
    } else {
      // TODO: Implement native image picker
      Alert.alert(
        t('screens.myProfile.addPhotoTitle', 'Dodaj zdjęcie'),
        t(
          'screens.myProfile.addPhotoMessage',
          'Funkcja dodawania zdjęć będzie dostępna wkrótce',
        ),
      );
    }
  }, [t]);

  const handleFileInputChange = useCallback(
    async (event: any) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        showErrorToast(
          t('common.error', 'Błąd'),
          t(
            'screens.myProfile.invalidFileType',
            'Nieprawidłowy typ pliku. Dozwolone: JPEG, PNG, GIF, WebP',
          ),
        );
        return;
      }

      // Validate file size (5MB for base64)
      if (file.size > 5 * 1024 * 1024) {
        showErrorToast(
          t('common.error', 'Błąd'),
          t(
            'screens.myProfile.fileTooLarge',
            'Plik jest za duży. Maksymalny rozmiar: 5MB',
          ),
        );
        return;
      }

      try {
        // Convert file to base64
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64Data = reader.result as string;
          // Remove data URL prefix (data:image/jpeg;base64,)
          const base64String = base64Data.split(',')[1] || base64Data;

          uploadPhoto.mutate({
            base64Data: base64String,
            originalName: file.name,
            mimeType: file.type,
            isDefault: (profile?.photos?.length || 0) === 0, // First photo is default
          });
        };
        reader.onerror = () => {
          showErrorToast(
            t('common.error', 'Błąd'),
            t(
              'screens.myProfile.fileReadError',
              'Nie udało się odczytać pliku',
            ),
          );
        };
        reader.readAsDataURL(file);
      } catch (error) {
        showErrorToast(
          t('common.error', 'Błąd'),
          t(
            'screens.myProfile.photoUploadError',
            'Nie udało się dodać zdjęcia',
          ),
        );
      }
    },
    [uploadPhoto, profile?.photos?.length, t],
  );

  const handleTogglePhotoLock = useCallback(
    (photoId: number, isCurrentlyLocked: boolean) => {
      if (isCurrentlyLocked) {
        unlockPhoto.mutate({ photoId });
      } else {
        lockPhoto.mutate({ photoId });
      }
    },
    [lockPhoto, unlockPhoto],
  );

  const toggleGenderPreference = useCallback((genderId: number) => {
    setSelectedGenderPreferenceIds(prev => {
      if (prev.includes(genderId)) {
        return prev.filter(id => id !== genderId);
      }
      return [...prev, genderId];
    });
  }, []);

  const toggleCity = useCallback((cityId: number) => {
    setSelectedCityIds(prev => {
      if (prev.includes(cityId)) {
        return prev.filter(id => id !== cityId);
      }
      return [...prev, cityId];
    });
    // Clear other city when selecting predefined city
    setOtherCity(null);
  }, []);

  const handleOpenCityModal = useCallback(() => {
    setIsCityModalVisible(true);
  }, []);

  const handleCloseCityModal = useCallback(() => {
    setIsCityModalVisible(false);
  }, []);

  const handleSelectOtherCity = useCallback((city: OtherCityData) => {
    setOtherCity(city);
    // Clear predefined cities when selecting custom city
    setSelectedCityIds([]);
  }, []);

  // Date picker handlers
  const openDatePicker = useCallback(() => {
    if (dateOfBirth) {
      setSelectedDay(dateOfBirth.getDate());
      setSelectedMonth(dateOfBirth.getMonth() + 1);
      setSelectedYear(dateOfBirth.getFullYear());
    }
    setShowDatePicker(true);
  }, [dateOfBirth]);

  const closeDatePicker = useCallback(() => {
    setShowDatePicker(false);
  }, []);

  const confirmDateSelection = useCallback(() => {
    const newDate = new Date(selectedYear, selectedMonth - 1, selectedDay);
    setDateOfBirth(newDate);
    setShowDatePicker(false);
  }, [selectedDay, selectedMonth, selectedYear]);

  const formatDateOfBirth = useCallback((date: Date | null): string => {
    if (!date) return '';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  }, []);

  // Generate arrays for date picker
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - 18 - i);

  // Colors
  const bgColor = isDark ? COLORS.BLACK : '#f5f5f5';
  const cardBg = isDark ? '#1a1a1a' : COLORS.WHITE;
  const textColor = isDark ? '#ffffff' : '#18181b';
  const subtitleColor = isDark ? '#a1a1aa' : '#71717a';
  const borderColor = isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)';
  const chipBgSelected = COLORS.PRIMARY;
  const chipBgUnselected = isDark ? '#27272a' : '#f4f4f5';
  const chipTextSelected = '#ffffff';
  const chipTextUnselected = isDark ? '#a1a1aa' : '#71717a';

  // Loading state
  if (isLoadingProfile) {
    return (
      <YStack
        flex={1}
        backgroundColor="$background"
        alignItems="center"
        justifyContent="center"
      >
        <ActivityIndicator size="large" color={COLORS.PRIMARY} />
      </YStack>
    );
  }

  return (
    <Screen edges={['top']}>
      <YStack flex={1} backgroundColor={bgColor}>
        {/* Header */}
        <ScreenHeader
          title={t('screens.myProfile.title', 'Mój profil')}
          onBackPress={handleGoBack}
        />

        <ScrollView
          contentContainerStyle={
            Platform.OS === 'web' && !isMobileLayout
              ? { paddingTop: 16, paddingBottom: 120 }
              : { paddingTop: 16, paddingBottom: 120, paddingHorizontal: 16 }
          }
          showsVerticalScrollIndicator={false}
        >
          {Platform.OS === 'web' && !isMobileLayout ? (
            // Desktop/Tablet: Two-column layout with cards
            <YStack
              padding="$4"
              gap="$4"
              maxWidth={1200}
              alignSelf="center"
              width="100%"
            >
              {/* Combined Card: Photos + Personal Info */}
              <YStack
                backgroundColor={cardBg}
                borderRadius="$4"
                padding="$4"
                borderWidth={1}
                borderColor={borderColor}
                gap="$4"
              >
                <XStack gap="$8" width="100%">
                  {/* Left Column - Photo Gallery */}
                  <YStack flex={1} gap="$4">
                    <Text fontSize="$4" fontWeight="600" color={textColor}>
                      {t('screens.myProfile.photos', 'Zdjęcia')}
                    </Text>
                    {Platform.OS === 'web' && (
                      <input
                        ref={el => {
                          fileInputRef.current = el;
                        }}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        style={{ display: 'none' }}
                        onChange={handleFileInputChange}
                      />
                    )}
                    {/* Photo Grid - 2 columns */}
                    <YStack gap="$4">
                      {(() => {
                        // Sort photos: default first, then others
                        const photos = [...(profile?.photos || [])].sort(
                          (a, b) => {
                            if (a.isDefault) return -1;
                            if (b.isDefault) return 1;
                            return 0;
                          },
                        );
                        const rows: (typeof photos)[] = [];
                        for (let i = 0; i < photos.length; i += 2) {
                          rows.push(photos.slice(i, i + 2));
                        }
                        return (
                          <>
                            {rows.map((row, rowIndex) => (
                              <XStack key={rowIndex} gap="$4" width="100%">
                                {row.map((photo, index) => {
                                  const photoIndex = rowIndex * 2 + index;
                                  const isFirstPhoto =
                                    photo.isDefault || photoIndex === 0;
                                  const isLocked = photo.isLocked;
                                  return (
                                    <YStack
                                      key={photo.id}
                                      position="relative"
                                      flex={1}
                                    >
                                      <AuthenticatedImage
                                        uri={
                                          isLocked
                                            ? photo.mediumBlurUrl
                                            : photo.mediumUrl
                                        }
                                        style={photoGridStyle}
                                      />
                                      {/* Blur overlay for locked photos */}
                                      {isLocked && (
                                        <YStack
                                          position="absolute"
                                          top={0}
                                          left={0}
                                          right={0}
                                          bottom={0}
                                          backgroundColor="rgba(255,255,255,0.7)"
                                          borderRadius={8}
                                          alignItems="center"
                                          justifyContent="center"
                                        >
                                          <Lock size={24} color={textColor} />
                                        </YStack>
                                      )}
                                      {/* Action buttons in bottom right corner */}
                                      <XStack
                                        position="absolute"
                                        bottom={-8}
                                        right={-8}
                                        gap={4}
                                      >
                                        {/* Lock/Unlock button - hidden for first photo */}
                                        {!isFirstPhoto && (
                                          <TouchableOpacity
                                            onPress={() =>
                                              handleTogglePhotoLock(
                                                photo.id,
                                                isLocked,
                                              )
                                            }
                                            style={actionButtonStyle}
                                          >
                                            {isLocked ? (
                                              <LockOpen
                                                size={12}
                                                color="white"
                                              />
                                            ) : (
                                              <Lock size={12} color="white" />
                                            )}
                                          </TouchableOpacity>
                                        )}
                                        {/* Set Default button */}
                                        <TouchableOpacity
                                          onPress={() =>
                                            handleSetDefaultPhoto(photo.id)
                                          }
                                          style={actionButtonStyle}
                                        >
                                          <Star
                                            size={12}
                                            color={
                                              photo.isDefault
                                                ? '#FFD700'
                                                : 'white'
                                            }
                                            fill={
                                              photo.isDefault
                                                ? '#FFD700'
                                                : 'none'
                                            }
                                          />
                                        </TouchableOpacity>
                                        {/* Delete button */}
                                        <TouchableOpacity
                                          onPress={() =>
                                            handleDeletePhoto(photo.id)
                                          }
                                          style={actionButtonStyle}
                                        >
                                          <X size={12} color="white" />
                                        </TouchableOpacity>
                                      </XStack>
                                    </YStack>
                                  );
                                })}
                                {/* Add empty space if odd number of photos in row */}
                                {row.length === 1 && <YStack flex={1} />}
                              </XStack>
                            ))}
                            {/* Add Photo Button row if less than max photos */}
                            {(!profile?.photos ||
                              profile.photos.length < 10) && (
                              <XStack gap="$4" width="100%">
                                <TouchableOpacity
                                  onPress={handleAddPhoto}
                                  style={addPhotoGridButtonStyle(isDark)}
                                >
                                  <Plus size={24} color={subtitleColor} />
                                </TouchableOpacity>
                                <YStack flex={1} />
                              </XStack>
                            )}
                          </>
                        );
                      })()}
                      {/* Add Photo Text Button */}
                      <TouchableOpacity
                        onPress={handleAddPhoto}
                        style={addPhotoTextButtonStyle(isDark, borderColor)}
                      >
                        <Text
                          color={COLORS.PRIMARY}
                          fontSize="$4"
                          fontWeight="500"
                        >
                          {t('screens.myProfile.addPhoto', 'Dodaj zdjęcie')}
                        </Text>
                      </TouchableOpacity>
                    </YStack>
                  </YStack>

                  {/* Right Column - Personal Info */}
                  <YStack flex={1} gap="$4">
                    {/* Nickname */}
                    <YStack space="$2">
                      <Text fontSize="$4" fontWeight="600" color={textColor}>
                        {t('screens.myProfile.nickname', 'Nickname/pseudonim')}
                      </Text>
                      <RNTextInput
                        value={nickName}
                        editable={false}
                        placeholder={t(
                          'screens.myProfile.nicknamePlaceholder',
                          'Twój pseudonim',
                        )}
                        placeholderTextColor={subtitleColor}
                        style={getInputStyle(theme, isDark, borderColor)}
                      />
                    </YStack>

                    {/* First Name */}
                    <YStack space="$2">
                      <Text fontSize="$4" fontWeight="600" color={textColor}>
                        {t('screens.myProfile.firstName', 'Imię')}
                      </Text>
                      <RNTextInput
                        value={firstName}
                        onChangeText={setFirstName}
                        placeholder={t(
                          'screens.myProfile.firstNamePlaceholder',
                          'Twoje imię',
                        )}
                        placeholderTextColor={subtitleColor}
                        style={getInputStyle(theme, isDark, borderColor)}
                      />
                    </YStack>

                    {/* Last Name */}
                    <YStack space="$2">
                      <Text fontSize="$4" fontWeight="600" color={textColor}>
                        {t('screens.myProfile.lastName', 'Nazwisko')}
                      </Text>
                      <RNTextInput
                        value={lastName}
                        onChangeText={setLastName}
                        placeholder={t(
                          'screens.myProfile.lastNamePlaceholder',
                          'Twoje nazwisko',
                        )}
                        placeholderTextColor={subtitleColor}
                        style={getInputStyle(theme, isDark, borderColor)}
                      />
                    </YStack>

                    {/* BIO */}
                    <YStack space="$2">
                      <Text fontSize="$4" fontWeight="600" color={textColor}>
                        {t('screens.myProfile.bio', 'BIO')}
                      </Text>
                      <RNTextInput
                        value={bio}
                        onChangeText={setBio}
                        placeholder={t(
                          'screens.myProfile.bioPlaceholder',
                          'O mnie',
                        )}
                        placeholderTextColor={subtitleColor}
                        multiline
                        numberOfLines={3}
                        style={getTextAreaStyle(theme, isDark, borderColor)}
                      />
                    </YStack>

                    {/* Date of Birth */}
                    <YStack space="$2">
                      <Text fontSize="$4" fontWeight="600" color={textColor}>
                        {t('screens.myProfile.dateOfBirth', 'Data urodzenia')}
                      </Text>
                      <TouchableOpacity onPress={openDatePicker}>
                        <RNTextInput
                          value={formatDateOfBirth(dateOfBirth)}
                          editable={false}
                          placeholder={t(
                            'screens.myProfile.dateOfBirthPlaceholder',
                            'Wybierz datę urodzenia',
                          )}
                          placeholderTextColor={subtitleColor}
                          style={getInputStyle(theme, isDark, borderColor)}
                        />
                      </TouchableOpacity>
                    </YStack>
                  </YStack>
                </XStack>
              </YStack>

              {/* Other Cards */}
              <XStack gap="$4" width="100%" justifyContent="space-between">
                {/* Preferences Card */}
                <YStack flex={1} maxWidth="50%" gap="$4">
                  <YStack
                    backgroundColor={cardBg}
                    borderRadius="$4"
                    padding="$4"
                    borderWidth={1}
                    borderColor={borderColor}
                    gap="$4"
                  >
                    {/* Gender Selection */}
                    <YStack space="$2">
                      <Text fontSize="$4" fontWeight="600" color={textColor}>
                        {t('screens.myProfile.gender', 'Płeć')}
                      </Text>
                      <XStack flexWrap="wrap" gap="$2">
                        {isLoadingGenders ? (
                          <ActivityIndicator
                            size="small"
                            color={COLORS.PRIMARY}
                          />
                        ) : (
                          genders.map(gender => (
                            <TouchableOpacity
                              key={gender.id}
                              onPress={() => setSelectedGenderId(gender.id)}
                              style={getChipStyle(
                                selectedGenderId === gender.id,
                                chipBgSelected,
                                chipBgUnselected,
                                borderColor,
                              )}
                            >
                              <Text
                                color={
                                  selectedGenderId === gender.id
                                    ? chipTextSelected
                                    : chipTextUnselected
                                }
                                fontSize="$3"
                                fontWeight="500"
                              >
                                {gender.name}
                              </Text>
                            </TouchableOpacity>
                          ))
                        )}
                      </XStack>
                    </YStack>

                    {/* Gender Preferences */}
                    <YStack space="$2">
                      <Text fontSize="$4" fontWeight="600" color={textColor}>
                        {t(
                          'screens.myProfile.genderPreferences',
                          'Kto Cię interesuje?',
                        )}
                      </Text>
                      <XStack flexWrap="wrap" gap="$2">
                        {isLoadingGenders ? (
                          <ActivityIndicator
                            size="small"
                            color={COLORS.PRIMARY}
                          />
                        ) : (
                          <>
                            {genders.map(gender => (
                              <TouchableOpacity
                                key={gender.id}
                                onPress={() =>
                                  toggleGenderPreference(gender.id)
                                }
                                style={getChipStyle(
                                  selectedGenderPreferenceIds.includes(
                                    gender.id,
                                  ),
                                  chipBgSelected,
                                  chipBgUnselected,
                                  borderColor,
                                )}
                              >
                                <Text
                                  color={
                                    selectedGenderPreferenceIds.includes(
                                      gender.id,
                                    )
                                      ? chipTextSelected
                                      : chipTextUnselected
                                  }
                                  fontSize="$3"
                                  fontWeight="500"
                                >
                                  {gender.name}
                                </Text>
                              </TouchableOpacity>
                            ))}
                            {/* "Wszyscy" option */}
                            <TouchableOpacity
                              onPress={() => {
                                if (
                                  selectedGenderPreferenceIds.length ===
                                  genders.length
                                ) {
                                  setSelectedGenderPreferenceIds([]);
                                } else {
                                  setSelectedGenderPreferenceIds(
                                    genders.map(g => g.id),
                                  );
                                }
                              }}
                              style={getChipStyle(
                                selectedGenderPreferenceIds.length ===
                                  genders.length,
                                chipBgSelected,
                                chipBgUnselected,
                                borderColor,
                              )}
                            >
                              <Text
                                color={
                                  selectedGenderPreferenceIds.length ===
                                  genders.length
                                    ? chipTextSelected
                                    : chipTextUnselected
                                }
                                fontSize="$3"
                                fontWeight="500"
                              >
                                {t('screens.myProfile.everyone', 'Wszyscy')}
                              </Text>
                            </TouchableOpacity>
                          </>
                        )}
                      </XStack>
                    </YStack>
                  </YStack>
                </YStack>

                {/* Hobby and City Card */}
                <YStack flex={1} maxWidth="50%" gap="$4">
                  <YStack
                    backgroundColor={cardBg}
                    borderRadius="$4"
                    padding="$4"
                    borderWidth={1}
                    borderColor={borderColor}
                    gap="$4"
                  >
                    {/* Hobby Selection */}
                    <YStack space="$2">
                      <Text fontSize="$4" fontWeight="600" color={textColor}>
                        {t('screens.myProfile.hobbies', 'Hobby')}
                      </Text>
                      <XStack flexWrap="wrap" gap="$2">
                        {isLoadingHobbies ? (
                          <ActivityIndicator
                            size="small"
                            color={COLORS.PRIMARY}
                          />
                        ) : (
                          hobbies.map(hobby => (
                            <TouchableOpacity
                              key={hobby.id}
                              onPress={() => {
                                if (selectedHobbyIds.includes(hobby.id)) {
                                  setSelectedHobbyIds(
                                    selectedHobbyIds.filter(
                                      id => id !== hobby.id,
                                    ),
                                  );
                                } else {
                                  setSelectedHobbyIds([
                                    ...selectedHobbyIds,
                                    hobby.id,
                                  ]);
                                }
                              }}
                              style={getChipStyle(
                                selectedHobbyIds.includes(hobby.id),
                                chipBgSelected,
                                chipBgUnselected,
                                borderColor,
                              )}
                            >
                              <Text
                                color={
                                  selectedHobbyIds.includes(hobby.id)
                                    ? chipTextSelected
                                    : chipTextUnselected
                                }
                                fontSize="$3"
                                fontWeight="500"
                              >
                                {hobby.name}
                              </Text>
                            </TouchableOpacity>
                          ))
                        )}
                      </XStack>
                    </YStack>

                    {/* City Selection */}
                    <YStack space="$2">
                      <Text fontSize="$4" fontWeight="600" color={textColor}>
                        {t('screens.myProfile.city', 'Miasto')}
                      </Text>
                      <XStack flexWrap="wrap" gap="$2">
                        {isLoadingCities ? (
                          <ActivityIndicator
                            size="small"
                            color={COLORS.PRIMARY}
                          />
                        ) : (
                          <>
                            {cities.map(city => (
                              <TouchableOpacity
                                key={city.id}
                                onPress={() => toggleCity(city.id)}
                                style={getChipStyle(
                                  selectedCityIds.includes(city.id),
                                  chipBgSelected,
                                  chipBgUnselected,
                                  borderColor,
                                )}
                              >
                                <Text
                                  color={
                                    selectedCityIds.includes(city.id)
                                      ? chipTextSelected
                                      : chipTextUnselected
                                  }
                                  fontSize="$3"
                                  fontWeight="500"
                                >
                                  {city.name}
                                </Text>
                              </TouchableOpacity>
                            ))}
                            {/* Custom city input option - last option for manual entry */}
                            <TouchableOpacity
                              onPress={handleOpenCityModal}
                              style={getChipStyle(
                                otherCity !== null,
                                chipBgSelected,
                                chipBgUnselected,
                                borderColor,
                              )}
                            >
                              <Text
                                color={
                                  otherCity !== null
                                    ? chipTextSelected
                                    : chipTextUnselected
                                }
                                fontSize="$3"
                                fontWeight="500"
                              >
                                {t('screens.myProfile.customCity', 'Inne')}
                              </Text>
                            </TouchableOpacity>
                          </>
                        )}
                      </XStack>

                      {/* Selected other city display */}
                      {otherCity && (
                        <YStack
                          backgroundColor={isDark ? '#27272a' : '#f4f4f5'}
                          padding="$3"
                          borderRadius="$3"
                          marginTop="$2"
                        >
                          <Text
                            fontSize="$3"
                            color={textColor}
                            fontWeight="500"
                          >
                            {otherCity.city ||
                              otherCity.displayName.split(',')[0]}
                          </Text>
                          <Text
                            fontSize="$2"
                            color={subtitleColor}
                            numberOfLines={2}
                          >
                            {[
                              otherCity.region,
                              otherCity.state,
                              otherCity.country,
                            ]
                              .filter(Boolean)
                              .join(', ')}
                          </Text>
                        </YStack>
                      )}
                    </YStack>
                  </YStack>
                </YStack>
              </XStack>

              {/* Action Buttons Card - Single column at bottom */}
              <YStack width="100%" gap="$4" marginTop="$4">
                <YStack
                  backgroundColor={cardBg}
                  borderRadius="$4"
                  padding="$4"
                  borderWidth={1}
                  borderColor={borderColor}
                  gap="$3"
                >
                  <TouchableOpacity
                    onPress={handleLogout}
                    disabled={logout.isPending}
                  >
                    <Text color={COLORS.PRIMARY} fontSize="$4" fontWeight="500">
                      {logout.isPending
                        ? t('common.loggingOut', 'Wylogowywanie...')
                        : t('screens.myProfile.logout', 'Wyloguj się')}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => {}}>
                    <Text color="#ef4444" fontSize="$4" fontWeight="500">
                      {t('screens.myProfile.deleteAccount', 'Usuń konto')}
                    </Text>
                  </TouchableOpacity>
                </YStack>
              </YStack>
            </YStack>
          ) : (
            // Mobile: Single column layout
            <YStack space="$5" paddingHorizontal="$4">
              {/* Combined Card: Photos + Personal Info */}
              <YStack
                backgroundColor={cardBg}
                borderRadius="$4"
                padding="$4"
                borderWidth={1}
                borderColor={borderColor}
                gap="$4"
              >
                {/* Photo Gallery */}
                <YStack gap="$4">
                  <Text fontSize="$4" fontWeight="600" color={textColor}>
                    {t('screens.myProfile.photos', 'Zdjęcia')}
                  </Text>
                  {Platform.OS === 'web' && (
                    <input
                      ref={el => {
                        fileInputRef.current = el;
                      }}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      style={{ display: 'none' }}
                      onChange={handleFileInputChange}
                    />
                  )}
                  {/* Photo Grid - 2 columns */}
                  <YStack gap="$4">
                    {(() => {
                      // Sort photos: default first, then others
                      const photos = [...(profile?.photos || [])].sort(
                        (a, b) => {
                          if (a.isDefault) return -1;
                          if (b.isDefault) return 1;
                          return 0;
                        },
                      );
                      const rows: (typeof photos)[] = [];
                      for (let i = 0; i < photos.length; i += 2) {
                        rows.push(photos.slice(i, i + 2));
                      }
                      return (
                        <>
                          {rows.map((row, rowIndex) => (
                            <XStack key={rowIndex} gap="$4" width="100%">
                              {row.map((photo, index) => {
                                const photoIndex = rowIndex * 2 + index;
                                const isFirstPhoto =
                                  photo.isDefault || photoIndex === 0;
                                const isLocked = photo.isLocked;
                                return (
                                  <YStack
                                    key={photo.id}
                                    position="relative"
                                    flex={1}
                                  >
                                    <AuthenticatedImage
                                      uri={
                                        isLocked
                                          ? photo.mediumBlurUrl
                                          : photo.mediumUrl
                                      }
                                      style={photoGridStyle}
                                    />
                                    {/* Blur overlay for locked photos */}
                                    {isLocked && (
                                      <YStack
                                        position="absolute"
                                        top={0}
                                        left={0}
                                        right={0}
                                        bottom={0}
                                        backgroundColor="rgba(255,255,255,0.7)"
                                        borderRadius={8}
                                        alignItems="center"
                                        justifyContent="center"
                                      >
                                        <Lock size={24} color={textColor} />
                                      </YStack>
                                    )}
                                    {/* Action buttons in bottom right corner */}
                                    <XStack
                                      position="absolute"
                                      bottom={-8}
                                      right={-8}
                                      gap={4}
                                    >
                                      {/* Lock/Unlock button - hidden for first photo */}
                                      {!isFirstPhoto && (
                                        <TouchableOpacity
                                          onPress={() =>
                                            handleTogglePhotoLock(
                                              photo.id,
                                              isLocked,
                                            )
                                          }
                                          style={actionButtonStyle}
                                        >
                                          {isLocked ? (
                                            <LockOpen size={12} color="white" />
                                          ) : (
                                            <Lock size={12} color="white" />
                                          )}
                                        </TouchableOpacity>
                                      )}
                                      {/* Set Default button */}
                                      <TouchableOpacity
                                        onPress={() =>
                                          handleSetDefaultPhoto(photo.id)
                                        }
                                        style={actionButtonStyle}
                                      >
                                        <Star
                                          size={12}
                                          color={
                                            photo.isDefault
                                              ? '#FFD700'
                                              : 'white'
                                          }
                                          fill={
                                            photo.isDefault ? '#FFD700' : 'none'
                                          }
                                        />
                                      </TouchableOpacity>
                                      {/* Delete button */}
                                      <TouchableOpacity
                                        onPress={() =>
                                          handleDeletePhoto(photo.id)
                                        }
                                        style={actionButtonStyle}
                                      >
                                        <X size={12} color="white" />
                                      </TouchableOpacity>
                                    </XStack>
                                  </YStack>
                                );
                              })}
                              {/* Add empty space if odd number of photos in row */}
                              {row.length === 1 && <YStack flex={1} />}
                            </XStack>
                          ))}
                          {/* Add Photo Button row if less than max photos */}
                          {(!profile?.photos || profile.photos.length < 10) && (
                            <XStack gap="$4" width="100%">
                              <TouchableOpacity
                                onPress={handleAddPhoto}
                                style={addPhotoGridButtonStyle(isDark)}
                              >
                                <Plus size={24} color={subtitleColor} />
                              </TouchableOpacity>
                              <YStack flex={1} />
                            </XStack>
                          )}
                        </>
                      );
                    })()}
                  </YStack>

                  {/* Add Photo Text Button */}
                  <TouchableOpacity
                    onPress={handleAddPhoto}
                    style={addPhotoTextButtonStyle(isDark, borderColor)}
                  >
                    <Text color={COLORS.PRIMARY} fontSize="$4" fontWeight="500">
                      {t('screens.myProfile.addPhoto', 'Dodaj zdjęcie')}
                    </Text>
                  </TouchableOpacity>
                </YStack>

                {/* Personal Info */}
                <YStack gap="$4">
                  {/* Nickname */}
                  <YStack space="$2">
                    <Text fontSize="$4" fontWeight="600" color={textColor}>
                      {t('screens.myProfile.nickname', 'Nickname/pseudonim')}
                    </Text>
                    <RNTextInput
                      value={nickName}
                      editable={false}
                      placeholder={t(
                        'screens.myProfile.nicknamePlaceholder',
                        'Twój pseudonim',
                      )}
                      placeholderTextColor={subtitleColor}
                      style={getInputStyle(theme, isDark, borderColor)}
                    />
                  </YStack>

                  {/* First Name */}
                  <YStack space="$2">
                    <Text fontSize="$4" fontWeight="600" color={textColor}>
                      {t('screens.myProfile.firstName', 'Imię')}
                    </Text>
                    <RNTextInput
                      value={firstName}
                      onChangeText={setFirstName}
                      placeholder={t(
                        'screens.myProfile.firstNamePlaceholder',
                        'Twoje imię',
                      )}
                      placeholderTextColor={subtitleColor}
                      style={getInputStyle(theme, isDark, borderColor)}
                    />
                  </YStack>

                  {/* Last Name */}
                  <YStack space="$2">
                    <Text fontSize="$4" fontWeight="600" color={textColor}>
                      {t('screens.myProfile.lastName', 'Nazwisko')}
                    </Text>
                    <RNTextInput
                      value={lastName}
                      onChangeText={setLastName}
                      placeholder={t(
                        'screens.myProfile.lastNamePlaceholder',
                        'Twoje nazwisko',
                      )}
                      placeholderTextColor={subtitleColor}
                      style={getInputStyle(theme, isDark, borderColor)}
                    />
                  </YStack>

                  {/* BIO */}
                  <YStack space="$2">
                    <Text fontSize="$4" fontWeight="600" color={textColor}>
                      {t('screens.myProfile.bio', 'BIO')}
                    </Text>
                    <RNTextInput
                      value={bio}
                      onChangeText={setBio}
                      placeholder={t(
                        'screens.myProfile.bioPlaceholder',
                        'O mnie',
                      )}
                      placeholderTextColor={subtitleColor}
                      multiline
                      numberOfLines={3}
                      style={getTextAreaStyle(theme, isDark, borderColor)}
                    />
                  </YStack>

                  {/* Date of Birth */}
                  <YStack space="$2">
                    <Text fontSize="$4" fontWeight="600" color={textColor}>
                      {t('screens.myProfile.dateOfBirth', 'Data urodzenia')}
                    </Text>
                    <TouchableOpacity onPress={openDatePicker}>
                      <RNTextInput
                        value={formatDateOfBirth(dateOfBirth)}
                        editable={false}
                        placeholder={t(
                          'screens.myProfile.dateOfBirthPlaceholder',
                          'Wybierz datę urodzenia',
                        )}
                        placeholderTextColor={subtitleColor}
                        style={getInputStyle(theme, isDark, borderColor)}
                      />
                    </TouchableOpacity>
                  </YStack>
                </YStack>
              </YStack>

              {/* Gender Selection */}
              <YStack space="$2">
                <Text fontSize="$4" fontWeight="500" color={textColor}>
                  {t('screens.myProfile.gender', 'Płeć')}
                </Text>
                <XStack flexWrap="wrap" gap="$2">
                  {isLoadingGenders ? (
                    <ActivityIndicator size="small" color={COLORS.PRIMARY} />
                  ) : (
                    genders.map(gender => (
                      <TouchableOpacity
                        key={gender.id}
                        onPress={() => setSelectedGenderId(gender.id)}
                        style={getChipStyle(
                          selectedGenderId === gender.id,
                          chipBgSelected,
                          chipBgUnselected,
                          borderColor,
                        )}
                      >
                        <Text
                          color={
                            selectedGenderId === gender.id
                              ? chipTextSelected
                              : chipTextUnselected
                          }
                          fontSize="$3"
                          fontWeight="500"
                        >
                          {gender.name}
                        </Text>
                      </TouchableOpacity>
                    ))
                  )}
                </XStack>
              </YStack>

              {/* Gender Preferences */}
              <YStack space="$2">
                <Text fontSize="$4" fontWeight="500" color={textColor}>
                  {t(
                    'screens.myProfile.genderPreferences',
                    'Kto Cię interesuje?',
                  )}
                </Text>
                <XStack flexWrap="wrap" gap="$2">
                  {isLoadingGenders ? (
                    <ActivityIndicator size="small" color={COLORS.PRIMARY} />
                  ) : (
                    <>
                      {genders.map(gender => (
                        <TouchableOpacity
                          key={gender.id}
                          onPress={() => toggleGenderPreference(gender.id)}
                          style={getChipStyle(
                            selectedGenderPreferenceIds.includes(gender.id),
                            chipBgSelected,
                            chipBgUnselected,
                            borderColor,
                          )}
                        >
                          <Text
                            color={
                              selectedGenderPreferenceIds.includes(gender.id)
                                ? chipTextSelected
                                : chipTextUnselected
                            }
                            fontSize="$3"
                            fontWeight="500"
                          >
                            {gender.name}
                          </Text>
                        </TouchableOpacity>
                      ))}
                      {/* "Wszyscy" option */}
                      <TouchableOpacity
                        onPress={() => {
                          if (
                            selectedGenderPreferenceIds.length ===
                            genders.length
                          ) {
                            setSelectedGenderPreferenceIds([]);
                          } else {
                            setSelectedGenderPreferenceIds(
                              genders.map(g => g.id),
                            );
                          }
                        }}
                        style={getChipStyle(
                          selectedGenderPreferenceIds.length === genders.length,
                          chipBgSelected,
                          chipBgUnselected,
                          borderColor,
                        )}
                      >
                        <Text
                          color={
                            selectedGenderPreferenceIds.length ===
                            genders.length
                              ? chipTextSelected
                              : chipTextUnselected
                          }
                          fontSize="$3"
                          fontWeight="500"
                        >
                          {t('screens.myProfile.everyone', 'Wszyscy')}
                        </Text>
                      </TouchableOpacity>
                    </>
                  )}
                </XStack>
              </YStack>

              {/* Hobby Selection */}
              <YStack space="$2">
                <Text fontSize="$4" fontWeight="500" color={textColor}>
                  {t('screens.myProfile.hobbies', 'Hobby')}
                </Text>
                <XStack flexWrap="wrap" gap="$2">
                  {isLoadingHobbies ? (
                    <ActivityIndicator size="small" color={COLORS.PRIMARY} />
                  ) : (
                    hobbies.map(hobby => (
                      <TouchableOpacity
                        key={hobby.id}
                        onPress={() => {
                          if (selectedHobbyIds.includes(hobby.id)) {
                            setSelectedHobbyIds(
                              selectedHobbyIds.filter(id => id !== hobby.id),
                            );
                          } else {
                            setSelectedHobbyIds([
                              ...selectedHobbyIds,
                              hobby.id,
                            ]);
                          }
                        }}
                        style={getChipStyle(
                          selectedHobbyIds.includes(hobby.id),
                          chipBgSelected,
                          chipBgUnselected,
                          borderColor,
                        )}
                      >
                        <Text
                          color={
                            selectedHobbyIds.includes(hobby.id)
                              ? chipTextSelected
                              : chipTextUnselected
                          }
                          fontSize="$3"
                          fontWeight="500"
                        >
                          {hobby.name}
                        </Text>
                      </TouchableOpacity>
                    ))
                  )}
                </XStack>
              </YStack>

              {/* City Selection */}
              <YStack space="$2">
                <Text fontSize="$4" fontWeight="500" color={textColor}>
                  {t('screens.myProfile.city', 'Miasto:')}{' '}
                  {selectedCityIds.length > 0 &&
                    cities
                      .filter(c => selectedCityIds.includes(c.id))
                      .map(c => c.name)
                      .join(', ')}
                </Text>
                <XStack flexWrap="wrap" gap="$2">
                  {isLoadingCities ? (
                    <ActivityIndicator size="small" color={COLORS.PRIMARY} />
                  ) : (
                    <>
                      {cities.map(city => (
                        <TouchableOpacity
                          key={city.id}
                          onPress={() => toggleCity(city.id)}
                          style={getChipStyle(
                            selectedCityIds.includes(city.id),
                            chipBgSelected,
                            chipBgUnselected,
                            borderColor,
                          )}
                        >
                          <Text
                            color={
                              selectedCityIds.includes(city.id)
                                ? chipTextSelected
                                : chipTextUnselected
                            }
                            fontSize="$3"
                            fontWeight="500"
                          >
                            {city.name}
                          </Text>
                        </TouchableOpacity>
                      ))}
                      {/* Custom city input option - last option for manual entry */}
                      <TouchableOpacity
                        onPress={handleOpenCityModal}
                        style={getChipStyle(
                          otherCity !== null,
                          chipBgSelected,
                          chipBgUnselected,
                          borderColor,
                        )}
                      >
                        <Text
                          color={
                            otherCity !== null
                              ? chipTextSelected
                              : chipTextUnselected
                          }
                          fontSize="$3"
                          fontWeight="500"
                        >
                          {t('screens.myProfile.customCity', 'Inne')}
                        </Text>
                      </TouchableOpacity>
                    </>
                  )}
                </XStack>

                {/* Selected other city display */}
                {otherCity && (
                  <YStack
                    backgroundColor={isDark ? '#27272a' : '#f4f4f5'}
                    padding="$3"
                    borderRadius="$3"
                    marginTop="$2"
                  >
                    <Text fontSize="$3" color={textColor} fontWeight="500">
                      {otherCity.city || otherCity.displayName.split(',')[0]}
                    </Text>
                    <Text fontSize="$2" color={subtitleColor} numberOfLines={2}>
                      {[otherCity.region, otherCity.state, otherCity.country]
                        .filter(Boolean)
                        .join(', ')}
                    </Text>
                  </YStack>
                )}
              </YStack>

              {/* Action Buttons Card - Single column at bottom */}
              <YStack width="100%" gap="$4" marginTop="$4">
                <YStack
                  backgroundColor={cardBg}
                  borderRadius="$4"
                  padding="$4"
                  borderWidth={1}
                  borderColor={borderColor}
                  gap="$3"
                >
                  <TouchableOpacity
                    onPress={handleLogout}
                    disabled={logout.isPending}
                  >
                    <Text color={COLORS.PRIMARY} fontSize="$4" fontWeight="500">
                      {logout.isPending
                        ? t('common.loggingOut', 'Wylogowywanie...')
                        : t('screens.myProfile.logout', 'Wyloguj się')}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => {}}>
                    <Text color="#ef4444" fontSize="$4" fontWeight="500">
                      {t('screens.myProfile.deleteAccount', 'Usuń konto')}
                    </Text>
                  </TouchableOpacity>
                </YStack>
              </YStack>
            </YStack>
          )}
        </ScrollView>

        {/* Save Button */}
        {Platform.OS === 'web' && !isMobileLayout ? (
          // Desktop/Tablet: Save button in separate row below columns
          <YStack
            padding="$4"
            backgroundColor="$background"
            borderTopWidth={1}
            borderTopColor={borderColor}
            maxWidth={1200}
            alignSelf="center"
            width="100%"
          >
            <TouchableOpacity
              onPress={handleSave}
              disabled={updateProfile.isPending}
              style={getSaveButtonStyle(updateProfile.isPending)}
            >
              <Text color="white" fontSize="$4" fontWeight="600">
                {updateProfile.isPending
                  ? t('common.saving', 'Zapisywanie...')
                  : t('common.save', 'Zapisz')}
              </Text>
            </TouchableOpacity>
          </YStack>
        ) : (
          // Mobile: Fixed bottom button
          <YStack
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            padding="$4"
            backgroundColor="$background"
            borderTopWidth={1}
            borderTopColor={borderColor}
          >
            <TouchableOpacity
              onPress={handleSave}
              disabled={updateProfile.isPending}
              style={getSaveButtonStyle(updateProfile.isPending)}
            >
              <Text color="white" fontSize="$4" fontWeight="600">
                {updateProfile.isPending
                  ? t('common.saving', 'Zapisywanie...')
                  : t('common.save', 'Zapisz')}
              </Text>
            </TouchableOpacity>
          </YStack>
        )}

        {/* City Search Modal */}
        <CitySearchModal
          visible={isCityModalVisible}
          onClose={handleCloseCityModal}
          onSelect={handleSelectOtherCity}
          initialValue={otherCity}
        />

        {/* Date Picker Modal */}
        <Modal
          visible={showDatePicker}
          transparent
          animationType="slide"
          onRequestClose={closeDatePicker}
        >
          <YStack
            flex={1}
            backgroundColor="rgba(0,0,0,0.5)"
            justifyContent="flex-end"
          >
            <YStack
              backgroundColor="$background"
              borderTopLeftRadius={20}
              borderTopRightRadius={20}
              padding="$4"
            >
              {/* Header */}
              <XStack
                justifyContent="space-between"
                alignItems="center"
                marginBottom="$4"
              >
                <Text fontSize="$5" fontWeight="600" color="$color">
                  {t('common.selectDateOfBirth', 'Wybierz datę urodzenia')}
                </Text>
                <TouchableOpacity onPress={closeDatePicker}>
                  <X size={24} color={theme.color?.val} />
                </TouchableOpacity>
              </XStack>

              {/* Date Pickers */}
              <XStack
                height={200}
                justifyContent="space-between"
                marginBottom="$4"
              >
                {/* Day */}
                <YStack flex={1} marginRight="$2">
                  <Text
                    fontSize="$3"
                    color="$color10"
                    textAlign="center"
                    marginBottom="$2"
                  >
                    {t('common.day', 'Dzień')}
                  </Text>
                  <FlatList
                    data={days}
                    keyExtractor={item => `day-${item}`}
                    showsVerticalScrollIndicator={false}
                    style={getPickerListStyle(theme)}
                    renderItem={({ item }) => (
                      <TouchableOpacity
                        onPress={() => setSelectedDay(item)}
                        style={getPickerItemStyle(item === selectedDay, theme)}
                      >
                        <Text
                          color={item === selectedDay ? 'white' : '$color'}
                          textAlign="center"
                          fontWeight={item === selectedDay ? '600' : '400'}
                        >
                          {item.toString().padStart(2, '0')}
                        </Text>
                      </TouchableOpacity>
                    )}
                  />
                </YStack>

                {/* Month */}
                <YStack flex={1} marginHorizontal="$1">
                  <Text
                    fontSize="$3"
                    color="$color10"
                    textAlign="center"
                    marginBottom="$2"
                  >
                    {t('common.month', 'Miesiąc')}
                  </Text>
                  <FlatList
                    data={months}
                    keyExtractor={item => `month-${item}`}
                    showsVerticalScrollIndicator={false}
                    style={getPickerListStyle(theme)}
                    renderItem={({ item }) => (
                      <TouchableOpacity
                        onPress={() => setSelectedMonth(item)}
                        style={getPickerItemStyle(
                          item === selectedMonth,
                          theme,
                        )}
                      >
                        <Text
                          color={item === selectedMonth ? 'white' : '$color'}
                          textAlign="center"
                          fontWeight={item === selectedMonth ? '600' : '400'}
                        >
                          {item.toString().padStart(2, '0')}
                        </Text>
                      </TouchableOpacity>
                    )}
                  />
                </YStack>

                {/* Year */}
                <YStack flex={1} marginLeft="$2">
                  <Text
                    fontSize="$3"
                    color="$color10"
                    textAlign="center"
                    marginBottom="$2"
                  >
                    {t('common.year', 'Rok')}
                  </Text>
                  <FlatList
                    data={years}
                    keyExtractor={item => `year-${item}`}
                    showsVerticalScrollIndicator={false}
                    style={getPickerListStyle(theme)}
                    renderItem={({ item }) => (
                      <TouchableOpacity
                        onPress={() => setSelectedYear(item)}
                        style={getPickerItemStyle(item === selectedYear, theme)}
                      >
                        <Text
                          color={item === selectedYear ? 'white' : '$color'}
                          textAlign="center"
                          fontWeight={item === selectedYear ? '600' : '400'}
                        >
                          {item}
                        </Text>
                      </TouchableOpacity>
                    )}
                  />
                </YStack>
              </XStack>

              {/* Confirm Button */}
              <TouchableOpacity
                onPress={confirmDateSelection}
                style={getConfirmButtonStyle()}
              >
                <Text color="white" fontSize="$4" fontWeight="600">
                  {t('common.confirm', 'Potwierdź')}
                </Text>
              </TouchableOpacity>
            </YStack>
          </YStack>
        </Modal>
      </YStack>
    </Screen>
  );
};

// Styles
const photoGridStyle: ImageStyle = {
  width: '100%',
  aspectRatio: 0.75, // 4:3 portrait ratio for taller photos
  borderRadius: 8,
};

const actionButtonStyle: ViewStyle = {
  width: 24,
  height: 24,
  borderRadius: 12,
  backgroundColor: '#B9849D',
  alignItems: 'center',
  justifyContent: 'center',
};

const addPhotoGridButtonStyle = (isDark: boolean): ViewStyle => ({
  width: '48%',
  aspectRatio: 1,
  borderRadius: 8,
  borderWidth: 2,
  borderStyle: 'dashed',
  borderColor: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)',
  alignItems: 'center',
  justifyContent: 'center',
});

const addPhotoTextButtonStyle = (
  _isDark: boolean,
  _borderColor: string,
): ViewStyle => ({
  paddingVertical: 12,
  paddingHorizontal: 16,
  borderRadius: 8,
  borderWidth: 1,
  borderColor: COLORS.PRIMARY,
  alignItems: 'center',
});

function getInputStyle(
  _theme: ReturnType<typeof useTheme>,
  isDark: boolean,
  borderColor: string,
): TextStyle {
  return {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: borderColor,
    color: isDark ? '#ffffff' : '#18181b',
    backgroundColor: isDark ? '#27272a' : '#ffffff',
    fontSize: 16,
  };
}

function getTextAreaStyle(
  _theme: ReturnType<typeof useTheme>,
  isDark: boolean,
  borderColor: string,
): TextStyle {
  return {
    ...getInputStyle(_theme, isDark, borderColor),
    minHeight: 80,
    textAlignVertical: 'top',
  };
}

function getPickerListStyle(theme: ReturnType<typeof useTheme>): ViewStyle {
  return {
    flex: 1,
    backgroundColor: theme.background?.val || '#ffffff',
    borderRadius: 8,
  };
}

function getPickerItemStyle(
  isSelected: boolean,
  _theme: ReturnType<typeof useTheme>,
): ViewStyle {
  return {
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: isSelected ? COLORS.PRIMARY : 'transparent',
    borderRadius: 6,
    marginVertical: 2,
  };
}

function getConfirmButtonStyle(): ViewStyle {
  return {
    backgroundColor: COLORS.PRIMARY,
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  };
}

function getChipStyle(
  isSelected: boolean,
  selectedBg: string,
  unselectedBg: string,
  borderColor: string,
): ViewStyle {
  return {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 20,
    backgroundColor: isSelected ? selectedBg : unselectedBg,
    borderWidth: isSelected ? 0 : 1,
    borderColor: borderColor,
  };
}

function getSaveButtonStyle(disabled: boolean): ViewStyle {
  return {
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 8,
    backgroundColor: disabled ? '#9ca3af' : COLORS.PRIMARY,
    alignItems: 'center',
  };
}

export default MyProfileScreen;
